import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { MapPin, Briefcase, Lock, ArrowRight } from "lucide-react";
import { api, fileUrl, formatDate } from "@/lib/api";
import { Stars, RatingSummary } from "@/components/Stars";
import { PackageCard } from "@/components/PackageCard";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { ProviderLogo, VerifiedBadge } from "@/components/ProviderCard";
import { PageLoader } from "@/components/layout/RequireAuth";

const Gallery = ({ photos }) => {
  const [open, setOpen] = useState(null);
  if (!photos.length) return <p className="text-sm text-stone-500">Belum ada foto portofolio.</p>;
  return (
    <>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3" data-testid="portfolio-gallery">
        {photos.map((p, i) => (
          <button key={p} onClick={() => setOpen(p)} data-testid={`portfolio-photo-${i}`}
            className={`overflow-hidden rounded-2xl bg-stone-100 ${i === 0 ? "col-span-2 row-span-2" : ""}`}>
            <img src={fileUrl(p)} alt={`Portofolio ${i + 1}`} loading="lazy" className="aspect-square h-full w-full object-cover transition-transform duration-500 hover:scale-[1.03]" />
          </button>
        ))}
      </div>
      <Dialog open={!!open} onOpenChange={() => setOpen(null)}>
        <DialogContent className="max-w-3xl overflow-hidden p-0" data-testid="portfolio-lightbox">
          <DialogTitle className="sr-only">Foto portofolio</DialogTitle>
          {open && <img src={fileUrl(open)} alt="" className="max-h-[80vh] w-full object-contain bg-stone-900" />}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default function ProviderProfile() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [packages, setPackages] = useState([]);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    api.get(`/providers/${slug}`).then((r) => setP(r.data)).catch(() => navigate("/"));
    api.get("/packages", { params: { provider: slug } }).then((r) => setPackages(r.data));
    api.get(`/providers/${slug}/reviews`).then((r) => setReviews(r.data));
  }, [slug, navigate]);

  if (!p) return <PageLoader />;
  const target = `/minta-penawaran?kategori=${p.subcategories[0] || ""}`;
  const showCta = !user || user.role === "pelanggan";

  return (
    <div data-testid="provider-profile-page">
      <div className="h-40 bg-teal-900 sm:h-52">
        {p.portfolio[0] && <img src={fileUrl(p.portfolio[0])} alt="" className="h-full w-full object-cover opacity-40" />}
      </div>
      <div className="container-page -mt-16 grid gap-8 pb-12 lg:grid-cols-[1fr_340px]">
        <div className="space-y-8">
          <div className="card-soft p-6 sm:p-8">
            <div className="flex flex-col gap-5 sm:flex-row sm:items-end">
              <div className="rounded-2xl bg-white p-1.5 shadow-md"><ProviderLogo provider={p} size="h-24 w-24" /></div>
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3">
                  <h1 className="text-3xl font-extrabold tracking-tight text-stone-900" data-testid="provider-name">{p.business_name}</h1>
                  {p.verified && <VerifiedBadge testId="provider-verified-badge" />}
                </div>
                <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-stone-500">
                  <span className="flex items-center gap-1.5" data-testid="provider-experience"><Briefcase className="h-4 w-4" /> {p.experience_years} tahun pengalaman</span>
                  <span className="flex items-center gap-1.5" data-testid="provider-cities"><MapPin className="h-4 w-4" /> {p.city_items.map((c) => c.name).join(", ")}</span>
                  <RatingSummary rating={p.rating} count={p.review_count} testId="provider-rating" />
                </div>
              </div>
            </div>
            <div className="mt-6 flex flex-wrap gap-2" data-testid="provider-subcategories">
              {p.subcategory_items.map((s) => (
                <Link key={s.slug} to={`/kategori/${s.slug}`} className="chip hover:border-teal-600 hover:text-teal-800">{s.name}</Link>
              ))}
            </div>
            <p className="mt-6 whitespace-pre-line leading-relaxed text-stone-600" data-testid="provider-description">{p.description}</p>
          </div>
          <div className="card-soft p-6 sm:p-8">
            <h2 className="text-xl font-bold text-stone-900">Portofolio</h2>
            <div className="mt-5"><Gallery photos={p.portfolio} /></div>
          </div>
          {packages.length > 0 && (
            <div data-testid="provider-packages">
              <h2 className="text-xl font-bold text-stone-900">Paket Harga Tetap</h2>
              <div className="mt-5 grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
                {packages.map((pk) => <PackageCard key={pk.package_id} pkg={pk} canBook={showCta} />)}
              </div>
            </div>
          )}
          <div className="card-soft p-6 sm:p-8" data-testid="provider-reviews">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-xl font-bold text-stone-900">Ulasan</h2>
              <RatingSummary rating={p.rating} count={p.review_count} testId="provider-reviews-summary" />
            </div>
            {reviews.length === 0 ? <p className="mt-4 text-sm text-stone-500">Belum ada ulasan.</p> : (
              <ul className="mt-5 divide-y divide-stone-100">
                {reviews.map((r) => (
                  <li key={r.review_id} className="py-4" data-testid={`review-${r.review_id}`}>
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-stone-100 text-xs font-bold text-stone-600">{r.reviewer_name[0]}</span>
                      <span className="text-sm font-semibold text-stone-900">{r.reviewer_name}</span>
                      <Stars value={r.rating} size="h-3.5 w-3.5" />
                      <span className="text-xs text-stone-400">{formatDate(r.created_at)}</span>
                    </div>
                    <p className="mt-2 text-sm leading-relaxed text-stone-600">{r.comment}</p>
                    <p className="mt-1 text-xs text-stone-400">{r.order_title}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <aside className="lg:pt-20">
          <div className="card-soft sticky top-24 space-y-4 p-6">
            <p className="text-lg font-bold text-stone-900">Tertarik dengan {p.business_name}?</p>
            <p className="text-sm text-stone-500">Kirim permintaan gratis. Penyedia yang sesuai kota dan kategori akan mengirim penawaran.</p>
            {showCta && (
              <Button asChild className="h-11 w-full rounded-xl bg-teal-700 font-semibold hover:bg-teal-800" data-testid="provider-request-cta">
                <Link to={user ? target : `/masuk?next=${encodeURIComponent(target)}`}>Minta Penawaran <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            )}
            <p className="flex items-start gap-2 rounded-xl bg-stone-50 p-3 text-xs text-stone-500" data-testid="provider-contact-hidden">
              <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Nomor WhatsApp dibagikan setelah Anda menerima penawaran dari penyedia ini.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}
